import type {
  TvShowListItemOutputDto,
  TvShowOneOutputDto,
  TvShowSearchListItemOutputDto,
  WebCreatorGlobalSearchTvShowDeepDto,
} from '@bldsoft/ovp-api-types'
import type {
  TvShow,
  TvShowEpisode,
  TvShowSeason,
  TvShowsGenre,
  TvShowsLanguage,
} from '@setplex/tria-api'
import { ContentTypeDefault, MediaTypes } from '@setplex/tria-api'
import { toDate } from '../../tools'
import { formatListItem } from '../collections/format'
import { format as formatCreator } from '../creators/format'
import { ApiHomeRowMixedType } from '../home/index.h'
import { formatFromList as formatTagFromList } from '../tags/format'
import type {
  ApiTvShow,
  ApiTvShowEpisode,
  ApiTvShowSeason,
  ApiTvShowsGenre,
  ApiTvShowsLanguage,
  ApiTvShowSuggestionItem,
} from './index.h'

export const formatMixedType = (
  type: ApiHomeRowMixedType
): ContentTypeDefault => {
  switch (type) {
    case ApiHomeRowMixedType.content:
      return ContentTypeDefault.movie
    case ApiHomeRowMixedType.tvshow:
      return ContentTypeDefault.tvshow
  }
}

const tvShowRequiredDefaults = (): Pick<
  TvShow,
  'createDate' | 'updateDate' | 'languages' | 'contentType'
> => ({
  createDate: new Date(0),
  updateDate: new Date(0),
  languages: [],
  contentType: MediaTypes.TV_SHOW,
})

const formatTagFromIdTitle = ({ id, title }: { id: number; title: string }) =>
  formatTagFromList({
    id,
    title,
    description: '',
    horizontalImage: '',
    background: [],
    createDate: '',
  })

export const formatFromOne = ({
  id,
  title,
  creator,
  isFavorite,
  poster,
  ageRating,
  userRating,
  languages,
  description,
  shortDescription,
  banner,
  releaseYears,
  seasonsCount,
  countries,
  genres,
  hashtags,
  crew,
  cast,
  ageRatingReasons,
}: TvShowOneOutputDto): TvShow => ({
  ...tvShowRequiredDefaults(),
  id,
  title: title || '',
  description: description || '',
  shortDescription: shortDescription || '',
  banner: banner || '',
  creator: creator ? formatCreator(creator) : undefined,
  isFavorite,
  poster: poster || '',
  ageRating: ageRating || '',
  userRating,
  releaseYears,
  seasonsCount,
  countries: [...countries],
  languages: languages.length ? languages.map(formatListItem) : [],
  genres: genres.length ? genres.map(formatTagFromIdTitle) : undefined,
  hashtags: hashtags.map(({ id, title }) => ({
    id,
    title,
    description: '',
    horizontalImage: '',
    verticalImage: '',
  })),
  crew: [...crew],
  cast: [...cast],
  ageRatingReasons: [...ageRatingReasons],
})

export const formatFromListItem = ({
  id,
  title,
  creator,
  isFavorite,
  poster,
  ageRating,
  userRating,
  languages,
  shortDescription,
  banner,
  releaseYears,
  seasonsCount,
  countries,
  genres,
}: TvShowListItemOutputDto): TvShow => ({
  ...tvShowRequiredDefaults(),
  id,
  title: title || '',
  shortDescription: shortDescription || '',
  banner: banner || '',
  creator: creator ? formatCreator(creator) : undefined,
  isFavorite,
  poster: poster || '',
  ageRating: ageRating || '',
  userRating,
  releaseYears,
  seasonsCount,
  countries: [...countries],
  languages: languages.length ? languages.map(formatListItem) : [],
  genres: genres.length ? genres.map(formatTagFromIdTitle) : undefined,
})

export const formatFromSearchListItem = ({
  id,
  title,
  creator,
  isFavorite,
  poster,
  ageRating,
  userRating,
  languages,
  banner,
}: TvShowSearchListItemOutputDto): TvShow => ({
  ...tvShowRequiredDefaults(),
  id,
  title: title || '',
  banner: banner || '',
  creator: creator ? formatCreator(creator) : undefined,
  isFavorite,
  poster: poster || '',
  ageRating: ageRating || '',
  userRating,
  languages: languages.length ? languages.map(formatListItem) : [],
})

export const formatFromSuggestionItem = ({
  id,
  title,
  poster,
  ageRating,
  userRating,
  banner,
}: ApiTvShowSuggestionItem): TvShow => ({
  ...tvShowRequiredDefaults(),
  id,
  title: title || '',
  banner: banner || '',
  isFavorite: false,
  poster: poster || '',
  ageRating: ageRating || '',
  userRating,
})

export const formatFromCreatorGlobalSearch = ({
  id,
  title,
  creator,
  isFavorite,
  poster,
  ageRating,
  userRating,
  languages,
  banner,
  updateDate,
}: WebCreatorGlobalSearchTvShowDeepDto): TvShow => ({
  ...tvShowRequiredDefaults(),
  id,
  title: title || '',
  banner: banner || '',
  creator: creator ? formatCreator(creator) : undefined,
  isFavorite,
  poster: poster || '',
  ageRating: ageRating || '',
  userRating,
  updateDate: toDate(updateDate),
  languages: languages.length ? languages.map(formatListItem) : [],
})

const isOneTvShow = (tvShow: ApiTvShow): tvShow is TvShowOneOutputDto =>
  'description' in tvShow && 'seasonsCount' in tvShow

const isSuggestionItem = (
  tvShow: ApiTvShow
): tvShow is ApiTvShowSuggestionItem =>
  !('languages' in tvShow) &&
  !('description' in tvShow) &&
  !('shortDescription' in tvShow)

const isSearchListItem = (
  tvShow: ApiTvShow
): tvShow is TvShowSearchListItemOutputDto =>
  !('description' in tvShow) && !('shortDescription' in tvShow)

const isCreatorGlobalSearchTvShow = (
  tvShow: ApiTvShow
): tvShow is WebCreatorGlobalSearchTvShowDeepDto =>
  'updateDate' in tvShow && !('description' in tvShow)

export const format = (tvShow: ApiTvShow): TvShow => {
  if (isCreatorGlobalSearchTvShow(tvShow)) {
    return formatFromCreatorGlobalSearch(tvShow)
  }

  if (isOneTvShow(tvShow)) {
    return formatFromOne(tvShow)
  }

  if (isSuggestionItem(tvShow)) {
    return formatFromSuggestionItem(tvShow)
  }

  if (isSearchListItem(tvShow)) {
    return formatFromSearchListItem(tvShow)
  }

  return formatFromListItem(tvShow)
}

export const formatGenre = ({
  id,
  title,
  description,
  seoDescription,
}: ApiTvShowsGenre | ApiTvShowsLanguage): TvShowsGenre | TvShowsLanguage => ({
  id: Number(id),
  title: title || '',
  description: description || '',
  seoDescription: seoDescription || '',
})

export const formatEpisode = (episode: ApiTvShowEpisode): TvShowEpisode => ({
  id: episode.id,
  ageRating: episode.ageRating || '',
  banner: episode.banner || '',
  crew: [...episode.crew],
  cast: [...episode.cast],
  title: episode.title || '',
  description: episode.description || '',
  duration: episode.duration,
  number: episode.number,
  ratingReasons: [...episode.ratingReasons],
  seasonId: episode.seasonId,
  seasonNumber: episode.seasonNumber,
  seasonTitle: episode.seasonTitle || '',
  shortDescription: episode.shortDescription || '',
  tvShowId: episode.tvShowId,
  tvShowTitle: episode.tvShowTitle || '',
})

export const formatSeason = (season: ApiTvShowSeason): TvShowSeason => ({
  id: season.id,
  title: season.title || '',
  banner: season.banner || '',
  description: season.description || '',
  number: season.number,
  episodesCount: season.episodesCount,
  releaseYear: season.releaseYear,
})
